'use client'

import { useEffect, useRef } from 'react'
import { useSafeTranslations } from '@/hooks/useSafeTranslations'
import { logger } from '@/lib/logger'
import './Hero.css'

// ==========================================================================
// HERO VIDEO — DEFINITIVE iOS-COMPATIBLE IMPLEMENTATION
//
// ARCHITECTURE: Declarative <source> elements + JS play-retry only
//
// WHY: Previous versions used JS-driven `video.src = ...` after React
// hydration. This creates a race condition on iOS Safari — the browser
// evaluates autoplay at initial render (when there's no src), and by the
// time JS assigns one, the autoplay opportunity has expired. This caused
// the video to silently fail on older iPhones (7, X) ~10 times in git
// history. See commits: d4c4531, 8bed193, bc67cec, 0321a2a, f6bd4d9, f2b2710
//
// HOW IT WORKS:
// 1. <source> elements are in JSX → browser sees playable content at render
// 2. Browser picks the first compatible source (HEVC if supported, else H.264)
// 3. The `media` attribute handles mobile/desktop source selection
// 4. An ultimate fallback <source> (no media, no codec) catches edge cases
// 5. JS only handles: play retry, seamless loop, error recovery, visibility
//
// VIDEO HOSTING: Served from Firebase Cloud Storage (GCS), NOT Firebase
// Hosting. Firebase Hosting auto-gzips all files including MP4. Chrome's
// <video> media stack can't decompress gzip (ERR_CONTENT_DECODING_FAILED).
// GCS serves with Content-Encoding: identity — no compression issues.
//
// NEVER CHANGE THIS TO video.src ASSIGNMENT — that's the recurring bug.
// NEVER MOVE VIDEOS BACK TO /public/videos/ — that's the gzip bug.
// ==========================================================================

// COPYRIGHT TAKEDOWN 2026-08-28: the aerial-ridge clip previously served from
// `/videos/hero-{desktop,mobile}-{h264,hevc}.mp4` was found to be
// copyright-encumbered. Those 4 objects were DELETED from the bucket (they now
// return 404) and the hero was reverted to the original clip — the skier in the
// helicopter — which lives at the bucket root and is the same footage that
// `/images/hero-poster.webp` was extracted from.
// DO NOT restore the `/videos/hero-*` paths. Local backup of the removed files:
// backups/video-copyright-2026-08-28/
const VIDEO_BASE = 'https://storage.googleapis.com/apex-experience-next.firebasestorage.app'
const VIDEO_V = '?v=3'

export default function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const fadeOverlayRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const t = useSafeTranslations('heroHome')

  // --- Scroll-out: fade + float up as hero scrolls away ---
  useEffect(() => {
    const content = contentRef.current
    if (!content) return

    let rafId: number

    const handleScroll = () => {
      rafId = requestAnimationFrame(() => {
        const scrollTop = window.scrollY
        const wh = window.innerHeight
        const start = wh * 0.4
        const end = wh * 0.88

        if (scrollTop <= start) {
          content.style.opacity = '1'
          content.style.transform = 'translateY(0px)'
        } else if (scrollTop >= end) {
          content.style.opacity = '0'
          content.style.transform = 'translateY(-52px)'
        } else {
          const raw = (scrollTop - start) / (end - start)
          const eased = raw * raw * (3 - 2 * raw) // smoothstep
          content.style.opacity = String(1 - eased)
          content.style.transform = `translateY(${-eased * 52}px)`
        }
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafId)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    let disposed = false
    let retryTimer: ReturnType<typeof setInterval> | null = null
    let hasLooped = false
    let loopFadeTimer: ReturnType<typeof setTimeout> | null = null
    let networkRetryCount = 0

    // CRITICAL: Force muted — React hydration doesn't always propagate this
    video.muted = true

    // --- Idempotent play: NO readyState gate ---
    // On iOS Safari (especially older versions), readyState may not advance
    // to HAVE_CURRENT_DATA (2) even when the video is technically playable.
    // Removing the gate lets the browser decide. If play() rejects, we retry.
    const tryPlay = async () => {
      if (disposed || !video.paused) return
      try {
        await video.play()
        logger.debug('Hero video playing')
        if (retryTimer) { clearInterval(retryTimer); retryTimer = null }
      } catch {
        // Expected errors — retry timer handles all:
        // - NotAllowedError: Low Power Mode or autoplay policy (unfixable)
        // - AbortError: play() interrupted by source loading
        // - NotSupportedError: no compatible source found yet
      }
    }

    // --- Seamless loop at ~23s with fade ---
    const checkVideoTime = () => {
      if (disposed) return
      const t = video.currentTime
      const fade = fadeOverlayRef.current
      if (t >= 22.0 && t < 22.5 && !hasLooped && fade) {
        fade.classList.add('fade-active')
      }
      if (t >= 23.0 && t < 23.5 && !hasLooped) {
        hasLooped = true
        video.currentTime = 0
        loopFadeTimer = setTimeout(() => {
          if (!disposed && fade) fade.classList.remove('fade-active')
          hasLooped = false
        }, 600)
      }
    }

    // --- Error handler: cache-bust retry for network/demuxer errors ---
    const handleError = () => {
      const error = video.error
      if (!error) return
      logger.error('Video error:', { code: error.code, message: error.message })

      // MEDIA_ERR_NETWORK (2): cached file corruption or demuxer failure
      // Retry with cache-bust query param to force fresh download
      if (error.code === 2 && networkRetryCount < 2) {
        networkRetryCount++
        logger.warn(`Network/demuxer error — retry ${networkRetryCount}/2`)
        const sources = video.querySelectorAll('source')
        sources.forEach(source => {
          const baseSrc = source.src.split('?')[0]
          source.src = `${baseSrc}?_cb=${Date.now()}`
        })
        video.load()
      }
      // MEDIA_ERR_DECODE (3) and MEDIA_ERR_SRC_NOT_SUPPORTED (4):
      // With declarative <source> elements, the browser automatically
      // tries the next source. No JS intervention needed.
    }

    // --- bfcache: resume when page restored from back/forward cache ---
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) tryPlay()
    }

    // --- Tab visibility: resume when tab becomes visible ---
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') tryPlay()
    }

    // --- Loop safety net: if timeupdate misses the 23s window ---
    const handleEnded = () => {
      if (disposed) return
      video.currentTime = 0
      tryPlay()
    }

    // CRITICAL: Attach ALL listeners BEFORE any playback attempt.
    // On cached page loads, the browser may resolve video from cache instantly
    // and fire canplay before this code runs. We handle that with the
    // immediate tryPlay() call below.
    video.addEventListener('canplay', tryPlay)
    video.addEventListener('loadeddata', tryPlay)
    video.addEventListener('loadedmetadata', tryPlay)
    video.addEventListener('timeupdate', checkVideoTime)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('error', handleError)
    window.addEventListener('pageshow', handlePageShow)
    document.addEventListener('visibilitychange', handleVisibility)

    // Immediate play attempt — handles cached/bfcache loads where
    // the video is already loaded before this useEffect runs
    tryPlay()

    // Safety net: retry every 500ms for 30 seconds.
    // Covers ALL edge cases: slow loading, iOS Safari quirks, bfcache,
    // tab switches, and race conditions between render and JS execution.
    // 30s is generous but necessary for older devices on slow connections.
    retryTimer = setInterval(tryPlay, 500)
    const safetyTimeout = setTimeout(() => {
      if (retryTimer) { clearInterval(retryTimer); retryTimer = null }
    }, 30_000)

    // IntersectionObserver: play/pause on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) tryPlay()
          else if (!video.paused) video.pause()
        })
      },
      { threshold: 0.25 }
    )
    observer.observe(video)

    // --- Full cleanup ---
    return () => {
      disposed = true
      if (retryTimer) clearInterval(retryTimer)
      clearTimeout(safetyTimeout)
      if (loopFadeTimer) clearTimeout(loopFadeTimer)
      observer.disconnect()
      video.removeEventListener('canplay', tryPlay)
      video.removeEventListener('loadeddata', tryPlay)
      video.removeEventListener('loadedmetadata', tryPlay)
      video.removeEventListener('timeupdate', checkVideoTime)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('error', handleError)
      window.removeEventListener('pageshow', handlePageShow)
      document.removeEventListener('visibilitychange', handleVisibility)
      video.pause()
    }
  }, [])

  return (
    <>
      <section className="hero" data-hero-background>
        <h1 className="visually-hidden">{t('seoH1')}</h1>
        <div className="video-container">
          {/* LCP Image — always visible behind video (z-index: 0) */}
          {/* Serves as poster when video can't play (Low Power Mode, etc.) */}
          <img
            src="/images/hero-poster.webp"
            alt="Apex Experience - Adventure in Chile"
            className="hero-poster-img"
            fetchPriority="high"
          />

          {/*
            DECLARATIVE <source> ELEMENTS — browser handles codec selection natively.

            Source cascade (browser tries top-to-bottom, picks first compatible):
            1. HEVC mobile (iPhone X+ with A11 hardware decoder, ~1.8MB)
            2. H.264 mobile (iPhone 7+ universal, ~4.7MB)
            3. HEVC desktop (Mac/modern browsers, ~3.2MB)
            4. H.264 desktop (universal desktop, ~7.3MB)
            5. Ultimate fallback (no media/codec filter — mobile H.264, plays everywhere)

            The `type` attribute with codec string lets the browser SKIP sources it
            can't play WITHOUT downloading them. iPhone 7 (A10, no HEVC hardware)
            skips HEVC sources → plays H.264 directly. No false-positive risk.

            The `media` attribute provides viewport-based source selection.

            preload="metadata" is used instead of "auto" because iOS Safari
            ignores "auto" but honors "metadata". The autoplay+muted+playsInline
            attributes handle actual loading/buffering.
          */}
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            playsInline
            preload="metadata"
          >
            {/* Original clip — H.264 only, no HEVC variants exist for it, so the
                cascade is simply mobile 720p → desktop 1080p → universal fallback. */}
            {/* Mobile: 720p H.264 (1280x720, 3.5MB) */}
            <source
              src={`${VIDEO_BASE}/hero-video-720p-mobile.mp4${VIDEO_V}`}
              type="video/mp4"
              media="(max-width: 768px)"
            />
            {/* Desktop: 1080p H.264 (1920x1080, 8.9MB) */}
            <source
              src={`${VIDEO_BASE}/hero-video-final.mp4${VIDEO_V}`}
              type="video/mp4"
              media="(min-width: 769px)"
            />
            {/* Desktop fallback: 1080p baseline profile — widest decoder support */}
            <source
              src={`${VIDEO_BASE}/hero-video-1080p-baseline.mp4${VIDEO_V}`}
              type="video/mp4"
              media="(min-width: 769px)"
            />
            {/* Ultimate fallback: no media query, no codec constraint */}
            <source src={`${VIDEO_BASE}/hero-video-720p-mobile.mp4${VIDEO_V}`} type="video/mp4" />
          </video>

          {/* Fade overlay for seamless loop */}
          <div ref={fadeOverlayRef} className="loop-fade-overlay"></div>

          {/* Dark overlay for contrast */}
          <div className="video-overlay"></div>
        </div>

        {/* Hero Content Overlay */}
        <div className="hero-content" ref={contentRef}>
          <div className="hero-text-container">
            <p className="hero-headline">{t('headline')}</p>
          </div>
          <div className="hero-ctas">
            <a href="/booking" className="hero-cta hero-cta-primary">
              {t('ctaPrimary')}
            </a>
            <a href="/booking/activities/" className="hero-cta hero-cta-secondary">
              {t('ctaSecondary')}
            </a>
          </div>
        </div>
      </section>
    </>
  )
}
